import React, { useState } from "react";
import clsx from "clsx";

import { ReactComponent as VideoDurationIcon } from "../../assets/svg/video-duration-icon.svg";
import { ReactComponent as ArrowDown } from "../../assets/svg/caret_down.svg";
import { ReactComponent as IncompleteMark } from "../../assets/svg/check-mark.svg";
import { ReactComponent as CompleteMark } from "../../assets/svg/check-circle.svg";
import { ReactComponent as PlayVideoIcon } from "../../assets/svg/play-video-icon.svg";
import { zeroPaddingText } from "../../libs/Util";
import Button from "../../atoms/Button";
import { VideoChapter, VideoChapterOPPA } from "../../state";

import styles from "./ChapterCard.module.scss";

interface OPPAWorkProps {
  onePagePortfolioAssessment: VideoChapterOPPA;
  workTitle: string;
}

interface ChapterCardProps {
  chapter: VideoChapter;
  retrospectiveWorkTitle?: string;
  isPlaying?: boolean;
  isInitialOpen?: boolean;
  handlePlayingButton: (id: number) => void;
}

const OPPAWork = ({ onePagePortfolioAssessment, workTitle }: OPPAWorkProps) => {
  return (
    <>
      {onePagePortfolioAssessment.title.includes(workTitle) && (
        <div className={styles.work}>
          <div className={styles.workTitleWrapper}>
            {onePagePortfolioAssessment.is_completed ? (
              <CompleteMark />
            ) : (
              <IncompleteMark />
            )}
            <p className={styles.workTitle}>{workTitle}</p>
          </div>
          {onePagePortfolioAssessment.one_page_portfolio_assessment_issues.map(
            oppaIssue => (
              <div key={oppaIssue.id} className={styles.workQuestion}>
                <p>{oppaIssue.title}</p>
                {oppaIssue.one_page_portfolio_assessment_questions.map(
                  oppaQuestion => (
                    <p key={oppaQuestion.id}>{oppaQuestion.title}</p>
                  ),
                )}
              </div>
            ),
          )}
        </div>
      )}
    </>
  );
};

const ChapterCard = ({
  chapter,
  retrospectiveWorkTitle,
  isPlaying,
  isInitialOpen,
  handlePlayingButton,
}: ChapterCardProps) => {
  const [isOpen, setIsOpen] = useState(isInitialOpen);
  const chapterCompleted =
    chapter.issues.every(issue => issue.is_completed) &&
    (chapter.one_page_portfolio_assessment?.is_completed ?? true);

  return (
    <div
      className={clsx(
        styles.container,
        chapterCompleted && styles.chapterCompleted,
      )}
    >
      <div
        className={clsx(
          styles.titleContainer,
          isOpen && styles.openedTitleContainer,
        )}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <p className={styles.title}>{chapter.title}</p>
        <div className={styles.duration}>
          <VideoDurationIcon />
          <p>{chapter.duration}</p>
        </div>
        {isPlaying && <p className={styles.displayPlaying}>再生中</p>}
        <ArrowDown
          className={clsx(styles.arrowUp, isOpen && styles.arrowDown)}
        />
      </div>
      {isOpen && (
        <div className={styles.detailContainer}>
          <div className={styles.workContainer}>
            <hr />
            {chapter.one_page_portfolio_assessment && (
              <OPPAWork
                onePagePortfolioAssessment={
                  chapter.one_page_portfolio_assessment
                }
                workTitle="Before WORK"
              />
            )}
            {chapter.issues.map(issue => (
              <div key={issue.id} className={styles.work}>
                <div className={styles.workTitleWrapper}>
                  {issue.is_completed ? <CompleteMark /> : <IncompleteMark />}
                  <p className={styles.workTitle}>
                    {issue.title === retrospectiveWorkTitle
                      ? "振り返り"
                      : zeroPaddingText(issue.title).replace("ワーク", "WORK")}
                  </p>
                </div>
                <p className={styles.workQuestion}>{issue.prompt}</p>
              </div>
            ))}
            {chapter.one_page_portfolio_assessment && (
              <OPPAWork
                onePagePortfolioAssessment={
                  chapter.one_page_portfolio_assessment
                }
                workTitle="After WORK"
              />
            )}
          </div>
          {!isPlaying && (
            <Button
              name="再生する"
              variant="normal"
              onClick={() => handlePlayingButton(chapter.id)}
              className={styles.playingButton}
            >
              <PlayVideoIcon />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ChapterCard;
